import { getFormErrorMessage } from "@core/utility/get-error-message";
import { UtilActivityTypeFormRule, UtilActivityTypeFormSchema } from "@factory/form/form.rule";
import { useFormDefaultUtilActivityType } from "@factory/form/form-default-util-activity-type.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { GetResponseUtilActivityTypeModel } from "@factory/model/query-model-util-activity-type";
import { 
  useQyDeleteUtilActivityType, 
  useQyGetUtilActivityTypeById, 
  useQyUpdateUtilActivityType} from "@factory/query/util-activity-type.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { zodResolver } from "@hookform/resolvers/zod";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { confirmDialog } from "primereact/confirmdialog";
import { FieldErrors, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

export function useEditUtilActivityType() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultUtilActivityType();
  const navigate = useNavigate();
  const { mapUpdateUtilActivityType, mapDeleteUtilActivityType } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { utilActivityTypeId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID UtilActivityType
  const handleGetApiSuccess = (data: GetResponseUtilActivityTypeModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

      setValue("name", responseData?.name || "");
      setValue("description", responseData?.description || "");
  };
  const {
    data: utilActivityTypeResponse,
    isLoading,
    isError: utilActivityTypeError,
  } = useQyGetUtilActivityTypeById(utilActivityTypeId || "", handleGetApiSuccess);
  const utilActivityTypeData = utilActivityTypeResponse?.data?.[0];

  // API UPDATE UtilActivityType
  const handleUpdateApiSuccess = () => {
    showSuccess("UtilActivityType updated successfully");
  };
  const { mutate: updateUtilActivityType, isLoading: isEditLoading } = useQyUpdateUtilActivityType(
    handleUpdateApiSuccess
  );

  // API DELETE UtilActivityType
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("UtilActivityType deleted successfully");
  };
  const { mutate: deleteUtilActivityType } = useQyDeleteUtilActivityType(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<UtilActivityTypeFormSchema>({
    defaultValues: getDefault(utilActivityTypeData),
    resolver: zodResolver(UtilActivityTypeFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: UtilActivityTypeFormSchema) => {
    if (!utilActivityTypeData || !utilActivityTypeData.code) {
      throw new Error("No utilActivityType data");
    }

    const formData = mapUpdateUtilActivityType(form, utilActivityTypeData.code);
    updateUtilActivityType(formData);
  };
  const handleValidateError = (err: FieldErrors<UtilActivityTypeFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!utilActivityTypeData || !utilActivityTypeData.code) {
      throw new Error("No utilActivityType data found");
    }

    const form = mapDeleteUtilActivityType(utilActivityTypeData.code);
    deleteUtilActivityType(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    utilActivityTypeId,
    utilActivityTypeData,
    isLoading,
    utilActivityTypeError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}